// Currency definitions for multi-currency display support.

export interface CurrencyInfo {
  code: string;
  name: string;
  symbol: string;
  /** Number of decimal places (0 for JPY/KRW/VND/IDR, 2 for most others) */
  decimals: number;
}

export const CURRENCY_LIST: readonly CurrencyInfo[] = [
  { code: 'USD', name: 'US Dollar', symbol: '$', decimals: 2 },
  { code: 'EUR', name: 'Euro', symbol: '\u20AC', decimals: 2 },
  { code: 'GBP', name: 'British Pound', symbol: '\u00A3', decimals: 2 },
  { code: 'JPY', name: 'Japanese Yen', symbol: '\u00A5', decimals: 0 },
  { code: 'KRW', name: 'South Korean Won', symbol: '\u20A9', decimals: 0 },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '\u00A5', decimals: 2 },
  { code: 'INR', name: 'Indian Rupee', symbol: '\u20B9', decimals: 2 },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', decimals: 2 },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$', decimals: 2 },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', decimals: 2 },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', decimals: 2 },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$', decimals: 2 },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', decimals: 2 },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', decimals: 2 },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', decimals: 2 },
  { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$', decimals: 2 },
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', decimals: 2 },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R', decimals: 2 },
  { code: 'TRY', name: 'Turkish Lira', symbol: '\u20BA', decimals: 2 },
  { code: 'RUB', name: 'Russian Ruble', symbol: '\u20BD', decimals: 2 },
  { code: 'PLN', name: 'Polish Zloty', symbol: 'z\u0142', decimals: 2 },
  { code: 'THB', name: 'Thai Baht', symbol: '\u0E3F', decimals: 2 },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp', decimals: 0 },
  { code: 'VND', name: 'Vietnamese Dong', symbol: '\u20AB', decimals: 0 },
  { code: 'TWD', name: 'New Taiwan Dollar', symbol: 'NT$', decimals: 2 },
  { code: 'AED', name: 'UAE Dirham', symbol: '\u062F.\u0625', decimals: 2 },
  { code: 'SAR', name: 'Saudi Riyal', symbol: '\uFDFC', decimals: 2 },
  { code: 'PHP', name: 'Philippine Peso', symbol: '\u20B1', decimals: 2 },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM', decimals: 2 },
  { code: 'CZK', name: 'Czech Koruna', symbol: 'K\u010D', decimals: 2 },
] as const;

export function getCurrencyByCode(code: string): CurrencyInfo | undefined {
  return CURRENCY_LIST.find((c) => c.code === code);
}

/**
 * Maps ISO 3166-1 alpha-2 country codes to their primary ISO 4217 currency code.
 * Only includes countries whose currency is in CURRENCY_LIST.
 * Countries not listed here default to USD.
 */
export const COUNTRY_CURRENCY_MAP: Readonly<Record<string, string>> = {
  // EUR zone
  AT: 'EUR',
  BE: 'EUR',
  CY: 'EUR',
  EE: 'EUR',
  FI: 'EUR',
  FR: 'EUR',
  DE: 'EUR',
  GR: 'EUR',
  IE: 'EUR',
  IT: 'EUR',
  LV: 'EUR',
  LT: 'EUR',
  LU: 'EUR',
  MT: 'EUR',
  NL: 'EUR',
  PT: 'EUR',
  SK: 'EUR',
  SI: 'EUR',
  ES: 'EUR',
  HR: 'EUR',
  // GBP
  GB: 'GBP',
  // JPY
  JP: 'JPY',
  // KRW
  KR: 'KRW',
  // CNY
  CN: 'CNY',
  // INR
  IN: 'INR',
  // BRL
  BR: 'BRL',
  // CAD
  CA: 'CAD',
  // AUD
  AU: 'AUD',
  // CHF
  CH: 'CHF',
  // HKD
  HK: 'HKD',
  // SGD
  SG: 'SGD',
  // SEK
  SE: 'SEK',
  // NOK
  NO: 'NOK',
  // MXN
  MX: 'MXN',
  // NZD
  NZ: 'NZD',
  // ZAR
  ZA: 'ZAR',
  // TRY
  TR: 'TRY',
  // RUB
  RU: 'RUB',
  // PLN
  PL: 'PLN',
  // THB
  TH: 'THB',
  // IDR
  ID: 'IDR',
  // VND
  VN: 'VND',
  // TWD
  TW: 'TWD',
  // AED
  AE: 'AED',
  // SAR
  SA: 'SAR',
  // PHP
  PH: 'PHP',
  // MYR
  MY: 'MYR',
  // CZK
  CZ: 'CZK',
};
